import { useEffect, useRef } from 'react';
import { Box, TablePagination, IconButton, Typography } from '@mui/material';
import { IconChevronLeft, IconChevronRight, IconPlayerSkipBack, IconPlayerSkipForward } from '@tabler/icons-react';
import PillSelect from 'common/PillSelect';
import CustomTooltip from 'common/customTooltip';

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const popperProps = {
  modifiers: [
    {
      name: "offset",
      options: {
        offset : [-17 , -15]
      }
    }
  ]
};

const CustomActions = ({ count, page, rowsPerPage, onPageChange }) => {
    const from = count === 0 ? 0 : page * rowsPerPage + 1;
    const to = Math.min(count, (page + 1) * rowsPerPage);
    const label = `${from} - ${to} of ${count}`;
    const isFirstPage = page === 0;
    const isLastPage = page >= Math.ceil(count / rowsPerPage) - 1;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flex: { xs: 1, sm: 'initial' } }}>
        <Box sx={{ mr: { xs: 'auto', sm: 3 } }}>
          <CustomTooltip 
            title="First Page"
            borderRadius="8px 0px 8px 8px"
            height="42px"
            popperProps={popperProps}
          >
            <IconButton aria-label="First page" onClick={(event) => onPageChange(event, 0)} disabled={isFirstPage} sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
              <IconPlayerSkipBack size={20} style={{ color: isFirstPage ? '#A0A3A7' : '#000' }} />
            </IconButton>
          </CustomTooltip>
        </Box>
        <CustomTooltip 
          title="Previous Page"
          borderRadius="8px 0px 8px 8px"
          height="42px"
          popperProps={popperProps}
        >
          <IconButton aria-label="Previous page" onClick={(event) => onPageChange(event, page - 1)} disabled={isFirstPage} sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
             <IconChevronLeft size={20} style={{ color: isFirstPage ? '#A0A3A7' : '#000' }} />
          </IconButton>
        </CustomTooltip>
        <Typography 
          sx={{
            color: '#111',
            fontFamily: "Noto Sans",
            fontSize: '0.875rem',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '21px',
            width: '7rem',
            textAlign: 'center'
          }}
        >
          {label}
        </Typography>
        <Box sx={{ mr: { xs: 'auto', sm: 3 } }}>
          <CustomTooltip 
            title="Next Page"
            borderRadius="8px 0px 8px 8px"
            height="42px"
            popperProps={popperProps}
          >
            <IconButton aria-label="Next page" onClick={(event) => onPageChange(event, page + 1)} disabled={isLastPage} sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
              <IconChevronRight size={20} style={{ color: isLastPage ? '#A0A3A7' : '#000' }} />
            </IconButton>
          </CustomTooltip>
        </Box>
        <CustomTooltip 
          title="Last Page"
          borderRadius="8px 0px 8px 8px"
          height="42px"
          popperProps={popperProps}
        >
          <IconButton aria-label="Last page" onClick={(event) => onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1))} disabled={isLastPage} sx={{ '&:hover': { backgroundColor: 'transparent' } }}>
             <IconPlayerSkipForward size={20} style={{ color: isLastPage ? '#A0A3A7' : '#000' }} />
          </IconButton>
        </CustomTooltip>
      </Box>
    );
  }

const CustomPagination = (props) => {
  const isFirstRender = useRef(true);
  
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    scrollToTop();
  }, [props.rowsPerPage, props.page]);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: { xs: 'column', sm: 'row' },
      alignItems: { xs: 'stretch', sm: 'center' },
      justifyContent: { xs: 'flex-start', sm: 'flex-end' },
      paddingTop: 3,
      gap: { xs: 3, sm: 0 },
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
      }}>
        <Typography
          sx={{
            color: '#000',
            fontFamily: 'Noto Sans',
            fontStyle: 'normal',
            fontSize: '14px',
            fontWeight: '600',
            lineHeight: { xs: '10px', sm: '21px' },
            height: { xs: '22px', sm: '21px' },
            mr: 2
          }}
        >
          Rows per page
        </Typography>
        <PillSelect
          value={props.rowsPerPage}
          onChange={(event) => {
            if (props.onRowsPerPageChange) {
              props.onRowsPerPageChange(event);
            } else if (props.onChangeRowsPerPage) {
              props.onChangeRowsPerPage(event);
            }
          }}
          options={[{ label: '10 rows', value: 10 }, { label: '20 rows', value: 20 }, { label: '50 rows', value: 50 }]}
          sx={{ width: { xs: '100%', sm: '10.875rem' } }}
        />
      </Box>
      <TablePagination
        {...props}
        rowsPerPageOptions={[]}
        ActionsComponent={CustomActions}
        sx={{
          width: { xs: '100%', sm: 'auto' },
          '& .MuiTablePagination-toolbar': {
            paddingLeft: { xs: 0, sm: 4 },
            minHeight: '48px',
            height: '48px'
          },
          '& .MuiTablePagination-displayedRows': {
            display: 'none',
          },
          '& .MuiTablePagination-spacer': {
            display: { xs: 'none', sm: 'block' },
          },
          '& .MuiTablePagination-actions': {
            flex: { xs: 1, sm: 'unset' },
            marginLeft: 0,
          }
        }}
      />
    </Box>
  );
};

export default CustomPagination;